import { defineStore } from 'pinia'
import { ref, type Ref } from 'vue'
import { useAuthStore } from './auth'
import api from '@/services/api'

export interface UserProfile {
  id: string
  name: string
  email: string
  role?: string
  avatar?: string
  phone?: string
  department?: string
  position?: string
  bio?: string
  preferences?: {
    emailNotifications: boolean
    pushNotifications: boolean
    language: string
    theme: string
  }
}

export const useProfileStore = defineStore('profile', () => {
  // State
  const profile: Ref<UserProfile | null> = ref(null)
  const isLoading: Ref<boolean> = ref(false)
  const error: Ref<string | null> = ref(null)

  // Actions
  async function fetchProfile(): Promise<void> {
    isLoading.value = true
    error.value = null
    try {
      const authStore = useAuthStore()
      if (!authStore.user) {
        throw new Error('User not authenticated')
      }

      // TODO: Replace with actual API endpoint when backend is ready
      // const data = await api.apiFetch('/api/profile')
      // profile.value = data

      // Mock data for now
      profile.value = {
        id: authStore.user.id,
        name: authStore.user.name,
        email: authStore.user.email,
        role: authStore.user.role || 'Plant Manager',
        avatar: localStorage.getItem('userAvatar') || undefined,
        phone: '+62 812-3456-7890',
        department: 'Maintenance',
        position: 'Senior Manager',
        bio: 'Experienced maintenance manager with 10+ years in heavy equipment management.',
        preferences: {
          emailNotifications: true,
          pushNotifications: true,
          language: localStorage.getItem('preferred-language') || 'en',
          theme: localStorage.getItem('theme') || 'light',
        },
      }
    } catch (err: any) {
      error.value = err.message || 'Failed to fetch profile'
      console.error('Failed to fetch profile:', err)
    } finally {
      isLoading.value = false
    }
  }

  async function updateProfile(updates: Partial<UserProfile>): Promise<void> {
    isLoading.value = true
    error.value = null
    try {
      // TODO: Replace with actual API endpoint when backend is ready
      // const data = await api.apiFetch('/api/profile', {
      //   method: 'PUT',
      //   body: JSON.stringify(updates)
      // })
      // profile.value = data

      // Mock update for now
      if (profile.value) {
        profile.value = {
          ...profile.value,
          ...updates,
        }
      }

      // Update auth store if name or email changed
      if (updates.name || updates.email) {
        const authStore = useAuthStore()
        if (authStore.user) {
          authStore.user = {
            ...authStore.user,
            ...(updates.name && { name: updates.name }),
            ...(updates.email && { email: updates.email }),
          }
        }
      }
    } catch (err: any) {
      error.value = err.message || 'Failed to update profile'
      console.error('Failed to update profile:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function uploadAvatar(file: File): Promise<string> {
    isLoading.value = true
    error.value = null
    try {
      // TODO: Replace with actual API endpoint when backend is ready
      // const formData = new FormData()
      // formData.append('avatar', file)
      // const data = await api.apiFetch('/api/profile/avatar', {
      //   method: 'POST',
      //   body: formData
      // })
      // const avatarUrl = data.url

      // Mock upload - convert to base64 for now
      const avatarUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => {
          const result = e.target?.result as string
          resolve(result)
        }
        reader.onerror = reject
        reader.readAsDataURL(file)
      })

      // Save to localStorage for persistence
      localStorage.setItem('userAvatar', avatarUrl)

      if (profile.value) {
        profile.value.avatar = avatarUrl
      }

      return avatarUrl
    } catch (err: any) {
      error.value = err.message || 'Failed to upload avatar'
      console.error('Failed to upload avatar:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function removeAvatar(): Promise<void> {
    isLoading.value = true
    error.value = null
    try {
      // TODO: Replace with actual API endpoint when backend is ready
      // await api.apiFetch('/api/profile/avatar', { method: 'DELETE' })

      // Mock removal
      localStorage.removeItem('userAvatar')
      if (profile.value) {
        profile.value.avatar = undefined
      }
    } catch (err: any) {
      error.value = err.message || 'Failed to remove avatar'
      console.error('Failed to remove avatar:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  return {
    profile,
    isLoading,
    error,
    fetchProfile,
    updateProfile,
    uploadAvatar,
    removeAvatar,
  }
})
